import axios from "axios";

import type { S3ServiceConfig } from "./s3-static-upload.service";

export interface PanchangamConfigService {
  createStaticUrl: (fileKey: string) => string;
  readJsonFromUrl: <T>(url: string) => Promise<T>;
  writeJsonToKey: (fileKey: string, value: unknown) => Promise<string>;
  writeJsonToStaticUrl: (url: string, value: unknown) => Promise<void>;
  toCdnKey: (url: string) => string;
}

export const createPanchangamConfigService = (config: S3ServiceConfig): PanchangamConfigService => {
  const { cdnUrl, presignBaseUrl, getAuthHeaders, transformPresignUrl, transformReadUrl, transformUploadUrl } = config;

  const createStaticUrl = (fileKey: string): string => {
    const normalizedKey = fileKey.replace(/^\/+/, "");
    return `${cdnUrl}/${normalizedKey}`;
  };

  const toCdnKey = (url: string): string => {
    const cdnPrefix = cdnUrl.endsWith("/") ? cdnUrl : `${cdnUrl}/`;
    const normalizedUrl = url.trim();

    if (normalizedUrl.startsWith(cdnPrefix)) {
      return normalizedUrl.slice(cdnPrefix.length).split("?")[0];
    }

    try {
      const parsed = new URL(normalizedUrl);
      return parsed.pathname.replace(/^\/+/, "").split("?")[0];
    } catch (_error) {
      return normalizedUrl.replace(/^\/+/, "").split("?")[0];
    }
  };

  const getPresignedUrl = async (fileKey: string): Promise<{ url: string; name: string }> => {
    const rawUrl = `${presignBaseUrl}${encodeURIComponent(fileKey)}`;
    const requestUrl = transformPresignUrl ? transformPresignUrl(rawUrl) : rawUrl;
    const response = await axios.get(requestUrl, { headers: getAuthHeaders() });
    const url = response?.data?.data?.url;
    const name = response?.data?.data?.attachmentId;

    if (!url || typeof url !== "string") {
      throw new Error("Failed to generate presigned url");
    }

    return { url, name };
  };

  const writeJsonToKey = async (fileKey: string, value: unknown): Promise<string> => {
    const body = JSON.stringify(value, null, 2);
    const normalizedKey = fileKey.replace(/^\/+/, "");
    const presignedUrl = await getPresignedUrl(normalizedKey);
    const uploadRequestUrl = transformUploadUrl ? transformUploadUrl(presignedUrl.url) : presignedUrl.url;

    await axios.put(uploadRequestUrl, body, {
      headers: { "Content-Type": "application/json" },
    });

    try {
      const s3Path = presignedUrl.name;
      const cdnBase = new URL(cdnUrl).origin;        // e.g. https://srm-sbox-cdn.a4b.io
      return `${cdnBase}/yoda/${s3Path}`;
    } catch {
      return createStaticUrl(normalizedKey);
    }
  };

  const readJsonFromUrl = async <T>(url: string): Promise<T> => {
    const readUrl = transformReadUrl ? transformReadUrl(url) : url;

    try {
      const response = await axios.get<T>(readUrl, {
        params: { cache_bust: Date.now() },
      });

      return response.data;
    } catch (_error) {
      throw new Error(`Unable to load JSON from: ${url}`);
    }
  };

  return {
    createStaticUrl,
    readJsonFromUrl,
    writeJsonToKey,
    writeJsonToStaticUrl: async (url, value) => {
      const fileKey = toCdnKey(url);
      await writeJsonToKey(fileKey, value);
    },
    toCdnKey,
  };
};
